import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Button,
  Icon,
  P,
  DataGrid,
  IconProps,
  Feedback,
  Lightbox,
  Badge,
  Stack,
} from '@fashionnovateam/nova-ui-toolkit';
import { Section } from '../ReusableComponents/NewDesign/Layout/Layout';
import Page from '../Page/index';
import Header from '../Header/index';
import PrinterPopover from '../ReusableComponents/PrinterConfiguration/PrinterPopover';
import StepSection from './StepSection';
import { VideoJS } from './VideoJS';
import { useGetPublicVideos } from '../../Lib/Hooks/videos.hooks';
import { PrintButtonCell } from '../PurchaseOrderDetail/PurchaseOrderDetailDataGridCells';
import { User } from '../../Lib/Api/vendor';
import {
  useGetPrintingOnboardingStatus,
  useUpdatePrintingOnboardingStatus,
} from '../../Lib/Hooks/printingOnboarding.hooks';
import { useBrowserPrint } from '../../Lib/Hooks/browserPrint.hooks';
import {
  generateTestZPL,
  PrintType,
} from '../../Lib/Utils/Asn/ZPLFileCreation/generate-test-label';
import { PrinterType } from '../ReusableComponents/PrinterConfiguration/types';
import UnitLabel from '../../Assets/images/unit.png';
import HangtagLabel from '../../Assets/images/hangtag.png';
import CartonLabel from '../../Assets/images/carton.png';
import {
  PrintingLabelType,
  PrintingPermission,
  PrintingPermissions,
  transformPrintingPermissions,
} from '../../Lib/Api/asn';

type PrintingSetupProps = {
  publicFilesUrls: {
    [key: string]: {
      read: string;
    };
  };
  user: User;
};

interface PrinterConfig {
  printerType: PrinterType;
}

type LightboxType = 'unit' | 'hangtag' | 'carton' | null;

interface LabelStatusConfig {
  key: PrintingLabelType;
  label: string;
  icon: IconProps['name'];
}

const LABEL_CONFIGS: LabelStatusConfig[] = [
  {
    key: 'unit_label',
    label: 'Unit Labels',
    icon: 'bag/paper-bag',
  },
  { key: 'hangtag', label: 'Hangtags', icon: 'tag' },
  {
    key: 'carton_asn',
    label: 'Carton Labels (ASN)',
    icon: 'package/package-02',
  },
  {
    key: 'carton_iccl',
    label: 'Carton Labels (ICCL)',
    icon: 'package/package-02',
  },
];

function LabelStatusBadge({ permission }: { permission?: PrintingPermission }) {
  if (!permission || permission.status === 'disabled') {
    return (
      <Badge color="neutral" contrast="low">
        <Icon
          name="multiple-cross/multiple-cross-cancel-circle"
          className="w-3.5 h-3.5"
          variant="solid"
        />
        Disabled
      </Badge>
    );
  }

  if (permission.status === 'approved') {
    return (
      <Badge color="green">
        <Icon
          name="check/check-tick-circle"
          className="w-3.5 h-3.5"
          variant="solid"
        />
        Approved
      </Badge>
    );
  }

  if (permission.status === 'pending_approval') {
    return (
      <Badge color="yellow" contrast="low" className="whitespace-nowrap">
        <Icon
          name="three-dots-menu/three-dots-menu-horizontal-circle"
          className="w-3.5 h-3.5"
          variant="solid"
        />
        Pending Approval
      </Badge>
    );
  }

  return null;
}

function StatusIcon({ status }: { status?: string }) {
  if (status === 'approved') {
    return (
      <Icon
        name="check/check-tick-circle"
        className="w-5 h-5 text-green-600"
        variant="solid"
      />
    );
  }
  if (status === 'pending_approval') {
    return <Icon name="circle-dotted" className="w-5 h-5 text-yellow-600" />;
  }
  return null;
}

function PrintPermissionsCard({
  permissions,
}: {
  permissions?: PrintingPermissions;
}) {
  return (
    <div className="border border-gray-200 rounded-lg p-6 shadow-sm">
      <P weight="medium" size="2xl" className="mb-3">
        Print Permissions
      </P>
      <P size="sm" color="secondary" className="mb-4">
        We&apos;ll email you each time a change is made to your printing
        permissions. You can always find them below as well.
      </P>
      <div className="space-y-3">
        {LABEL_CONFIGS.map((config) => {
          const permission = permissions?.[config.key];
          const isPending = permission?.status === 'pending_approval';
          const pendingReason = permission?.pending_approval_reason;

          return (
            <div
              key={config.key}
              className="bg-white border border-gray-200 rounded-lg"
            >
              <div className="flex flex-col gap-4 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-row gap-4 items-center">
                    <Icon
                      name={config.icon}
                      className="w-5 h-5 text-gray-600"
                    />
                    <P>{config.label}</P>
                  </div>
                  <LabelStatusBadge permission={permission} />
                </div>

                {isPending && pendingReason && (
                  <div className="flex items-start gap-3 p-3 bg-warning-100 rounded-lg">
                    <Icon
                      name="information/information-triangle"
                      variant="solid"
                      className="w-5 h-5 text-warning-700 mt-0.5"
                    />
                    <div className="flex flex-col gap-1">
                      <P size="sm">Message to Vendor</P>
                      <P size="xs" color="disabled">
                        &quot;{pendingReason}&quot;
                      </P>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PrintingSetupPageV2({
  publicFilesUrls,
  user,
}: PrintingSetupProps) {
  const unitLabelPhotos = [{ src: UnitLabel }];
  const hangtagPhotos = [{ src: HangtagLabel }];
  const cartonLabelPhotos = [{ src: CartonLabel }];

  const containerRef = useRef<HTMLDivElement>(null);
  const [isLightboxOpened, setIsLightboxOpened] = useState<LightboxType>(null);
  const { data: videoFiles } = useGetPublicVideos();

  const permissions = transformPrintingPermissions(
    user.vendor?.printing_permissions,
  );

  const stepOne = [
    {
      id: '1',
      text: 'Purchase the printers and print supplies from the authorized sellers.',
    },
    {
      id: '2',
      text: 'Install Zebra Print Drivers specific to the printer model purchased.',
    },
    {
      id: '3',
      text: 'Follow set up guidelines listed in the recommended driver support documentation.',
    },
  ];

  const stepTwo = [
    {
      id: '1',
      text: (
        <>
          Follow our installation guide for{' '}
          <a
            href={publicFilesUrls.mac_printing_guide?.read || '#'}
            className="text-primary-700 hover:text-primary-900"
          >
            Macintosh
          </a>{' '}
          or{' '}
          <a
            href={publicFilesUrls.windows_printing_guide?.read || '#'}
            className="text-primary-700 hover:text-primary-900"
          >
            PC.
          </a>
        </>
      ),
    },
    {
      id: '2',
      text: 'Once your printer is installed, configure each printer in the widget below. It will change from Not Set to the name of your printer.',
    },
  ];

  const stepThree = [
    {
      id: '1',
      text: 'Print the Unit Label, Hangtag and Carton Label below.',
    },
    {
      id: '2',
      text: 'Ship all of the printed test labels to us at:',
    },
  ];

  const { sendZplCmd, selectedDevices } = useBrowserPrint();

  const getPrinterConfig = (type: PrintType): PrinterConfig => {
    switch (type) {
      case 'unit':
        return {
          printerType: 'unitLabel',
        };
      case 'hangtag':
        return {
          printerType: 'hangTag',
        };
      case 'carton':
        return {
          printerType: 'cartonLabel',
        };
      default:
        return {
          printerType: 'unitLabel',
        };
    }
  };

  const generateAndPrintLabel = async (
    type: PrintType,
    sucessCallback: () => void,
  ) => {
    const labels = await generateTestZPL(user.id.toString(), type);
    if (!labels) return;

    const { printerType } = getPrinterConfig(type);

    console.log(`Test Label ZPL (${type}):`, labels);
    await sendZplCmd(
      user.id.toString(),
      printerType,
      labels?.zpl,
      labels?.request_id,
      sucessCallback,
      () => {
        toast.error(
          'Failed to print label. Please check your printing configuration and try again.',
        );
      },
    );
  };

  const updatePrintStatus = useUpdatePrintingOnboardingStatus();
  const { data: printStatus } = useGetPrintingOnboardingStatus(user.id);

  const handlePrintButtonClick = async (type: PrintType) => {
    try {
      const printerTypeMap: Record<PrintType, PrinterType> = {
        unit: 'unitLabel',
        carton: 'cartonLabel',
        hangtag: 'hangTag',
      };

      const printerType = printerTypeMap[type];
      const printerStatus = selectedDevices[printerType]?.status;

      if (printerStatus === 'offline') {
        toast.error('Printer is offline.');
        return;
      }

      if (printerStatus === 'not selected') {
        toast.error(
          'Your printer is not correctly configured or not ready to print.',
        );
        return;
      }

      if (printerStatus === 'selected') {
        toast.error('Your printer is not ready to print.');
        return;
      }

      await generateAndPrintLabel(type, async () => {
        await updatePrintStatus.mutateAsync({
          userId: user.id,
          data: {
            printed_samples: {
              ...(printStatus?.printed_samples ?? {
                unit: false,
                carton: false,
                hangtag: false,
              }),
              [type]: true,
            },
          },
        });
      });
    } catch (error) {
      console.error(`Error printing ${type}:`, error);
      toast.error(`Failed to print ${type} label. Please try again.`);
    }
  };

  return (
    <Page.Container>
      <Header.Major icon="printer" title="Printing Setup" />
      <Section>
        <Page.Content className="overflow-y-auto overflow-x-hidden">
          <PrintPermissionsCard permissions={permissions} />

          <div className="border-b border-gray-200 my-10" />

          <P
            weight="medium"
            className="text-[36px] leading-[40px] custom-letter-spacing"
          >
            Start printing in 3 easy steps.
          </P>

          <P size="xl" className="mt-6" color="secondary">
            Before you begin using printing on all POs, we need to complete a
            test run to ensure everything works as expected. Follow the steps
            below to get started.
          </P>
          <P size="md" className="mt-4">
            <a
              href="#videos"
              className="text-primary-700 hover:text-primary-900 underline"
            >
              Watch our video series
            </a>{' '}
            <span className="text-gray-900">
              showing you the full process end-to-end
            </span>
          </P>

          <div className="border-b border-gray-200 my-10" />

          <StepSection
            stepNumber="Step 1"
            stepTitle="Determine what you'd like to print:"
          />
          <P size="sm" color="secondary" className="mt-3">
            You can optionally print Unit Labels, hangtags and carton labels
            through your portal. Depending on which you wish to print will
            impact what printers you need to set up and which labels you need to
            send us for approval.
          </P>
          <Feedback
            title="Carton label printing will be required to be completed through your portal in 2026."
            description="We recommend getting ahead of this and getting approved as soon as possible."
            type="info"
            className="mt-4"
          />

          <div className="border-b border-gray-200 my-10" />

          <StepSection
            stepNumber="Step 2"
            stepTitle="Ensure you have the right printers and drivers."
            stepDescription={
              <>
                See{' '}
                <a
                  href={publicFilesUrls.printing_guide?.read || '#'}
                  className="text-primary-700 hover:text-primary-900 underline"
                >
                  our full guide
                </a>{' '}
                for Zebra Printers, Drivers & Guidelines
              </>
            }
            steps={stepOne}
          />

          <div className="border-b border-gray-200 my-10" />

          <StepSection
            stepNumber="Step 3"
            stepTitle="Set up your printer(s)"
            steps={stepTwo}
          />
          <div
            className="shadow-lg rounded-lg px-6 py-10 w-[527px] mt-6 relative"
            ref={containerRef}
          >
            <PrinterPopover containerRef={containerRef} />
          </div>

          <div className="border-b border-gray-200 my-10" />

          <StepSection
            stepNumber="Step 4"
            stepTitle="Print test labels and ship them to us"
          />
          <P size="sm" color="secondary" className="mt-3">
            We&apos;ll email you if you&apos;re approved or with corrections to
            make.
          </P>
          <div className="mt-4 ml-0">
            {stepThree.map((step) => (
              <div key={step.id} className="flex gap-3 items-start mb-4">
                <div className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded-full text-sm text-gray-600 shrink-0">
                  {step.id}
                </div>
                <P size="sm" color="secondary" className="pt-1">
                  {step.text}
                </P>
              </div>
            ))}
          </div>
          <div className="mt-3 ml-7 lg:ml-9">
            <P size="sm" weight="semibold">
              Attention - Mayra Gonzalez
            </P>
            <P size="sm" color="secondary">
              407 N Maple Dr
            </P>
            <P size="sm" color="secondary">
              Beverly Hills, CA 90210
            </P>
          </div>
          <Feedback
            title="If you have multiple locations where you print from, you must send a test from every location"
            description="This is a requirement for approval"
            type="warning"
            className="mt-10"
          />
          <div className="mt-10">
            <DataGrid
              paginationMode="client"
              hideFooter
              disableColumnReorder
              disableColumnResize
              autoHeight
              rows={[
                {
                  id: 1,
                  header: (
                    <P color="disabled" size="xs" weight="medium">
                      Print
                    </P>
                  ),
                  unit_label: (
                    <Stack direction="row" gap="sm" align="center">
                      <PrintButtonCell
                        printFn={() => handlePrintButtonClick('unit')}
                        isChecked={printStatus?.printed_samples?.unit || false}
                        printIcon="bag/paper-bag"
                      />
                      <StatusIcon status={permissions?.unit_label?.status} />
                    </Stack>
                  ),
                  hang_tags: (
                    <Stack direction="row" gap="sm" align="center">
                      <PrintButtonCell
                        printFn={() => handlePrintButtonClick('hangtag')}
                        isChecked={
                          printStatus?.printed_samples?.hangtag || false
                        }
                        printIcon="tag"
                      />
                      <StatusIcon status={permissions?.hangtag?.status} />
                    </Stack>
                  ),
                  carton_asn: (
                    <Stack direction="row" gap="sm" align="center">
                      <PrintButtonCell
                        printFn={() => handlePrintButtonClick('carton')}
                        isChecked={
                          printStatus?.printed_samples?.carton || false
                        }
                        printIcon="package/package-02"
                      />
                      <StatusIcon status={permissions?.carton_asn?.status} />
                    </Stack>
                  ),
                  carton_iccl: (
                    <Stack direction="row" gap="sm" align="center">
                      <PrintButtonCell
                        printFn={() => handlePrintButtonClick('carton')}
                        isChecked={
                          printStatus?.printed_samples?.carton || false
                        }
                        printIcon="package/package-02"
                      />
                      <StatusIcon status={permissions?.carton_iccl?.status} />
                    </Stack>
                  ),
                },
                {
                  id: 2,
                  header: (
                    <P color="disabled" size="xs" weight="medium">
                      Expected Output
                    </P>
                  ),
                  unit_label: (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        color="secondary"
                        onClick={() => setIsLightboxOpened('unit')}
                      >
                        Preview
                      </Button>
                      <Lightbox
                        slides={unitLabelPhotos}
                        isOpen={isLightboxOpened === 'unit'}
                        onClose={() => setIsLightboxOpened(null)}
                      />
                    </>
                  ),
                  hang_tags: (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        color="secondary"
                        onClick={() => setIsLightboxOpened('hangtag')}
                      >
                        Preview
                      </Button>
                      <Lightbox
                        slides={hangtagPhotos}
                        isOpen={isLightboxOpened === 'hangtag'}
                        onClose={() => setIsLightboxOpened(null)}
                      />
                    </>
                  ),
                  carton_asn: (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        color="secondary"
                        onClick={() => setIsLightboxOpened('carton')}
                      >
                        Preview
                      </Button>
                      <Lightbox
                        slides={cartonLabelPhotos}
                        isOpen={isLightboxOpened === 'carton'}
                        onClose={() => setIsLightboxOpened(null)}
                      />
                    </>
                  ),
                  carton_iccl: (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        color="secondary"
                        onClick={() => setIsLightboxOpened('carton')}
                      >
                        Preview
                      </Button>
                      <Lightbox
                        slides={cartonLabelPhotos}
                        isOpen={isLightboxOpened === 'carton'}
                        onClose={() => setIsLightboxOpened(null)}
                      />
                    </>
                  ),
                },
              ]}
              columns={[
                {
                  field: 'header',
                  headerName: '',
                  render: 'custom',
                  display: 'flex',
                  flex: 1,
                  sortable: false,
                  minWidth: 100,
                  disableColumnMenu: true,
                },
                {
                  field: 'unit_label',
                  headerName: 'Unit Labels',
                  render: 'custom',
                  display: 'flex',
                  flex: 1,
                  sortable: false,
                  minWidth: 120,
                  disableColumnMenu: true,
                },
                {
                  field: 'hang_tags',
                  headerName: 'Hangtags',
                  render: 'custom',
                  display: 'flex',
                  flex: 1,
                  sortable: false,
                  minWidth: 120,
                  disableColumnMenu: true,
                },
                {
                  field: 'carton_asn',
                  headerName: 'Carton Labels (ASN)',
                  render: 'custom',
                  display: 'flex',
                  flex: 1,
                  sortable: false,
                  minWidth: 140,
                  disableColumnMenu: true,
                },
                {
                  field: 'carton_iccl',
                  headerName: 'Carton Labels (ICCL)',
                  render: 'custom',
                  display: 'flex',
                  flex: 1,
                  sortable: false,
                  minWidth: 140,
                  disableColumnMenu: true,
                },
              ]}
            />
          </div>

          <div id="videos" className="border-b border-gray-200 my-10" />

          <P color="primary" size="md" weight="medium" className="mt-5">
            Watch our video series showing you the full process end-to-end.
          </P>
          <P color="secondary" size="md" className="mt-5">
            You can also view our{' '}
            <a
              href={publicFilesUrls.full_printing_guide?.read || '#'}
              className="text-primary-700 hover:text-primary-900 underline"
            >
              full Printing Guide here.
            </a>
          </P>
          <div className="mt-6">
            {videoFiles &&
              Object.entries(videoFiles).map(([key, video]) => {
                const videoJsOptions = {
                  autoplay: false,
                  controls: true,
                  responsive: true,
                  fluid: true,
                  poster: video.thumbnail,
                  sources: [
                    {
                      src: video.read,
                      type: 'video/mp4',
                    },
                  ],
                };

                return <VideoJS key={key} options={videoJsOptions} />;
              })}
          </div>
        </Page.Content>
      </Section>
    </Page.Container>
  );
}
